import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import { getAboutBanner, updateAboutBanner } from "../actions/about.actions";

// Common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import ToolBody from "../components/common/ToolBody";

/**
 * @returns {JSX.Element}
 * @component
 */
const About = () => {
  const { storage } = useContext(AppContext);

  // Initial state
  const bannerDefault = {
    about_banner_section_id: 0,
    banner_image: "",
    banner_image_name: "",
    banner_image_error: false,
    banner_image_mob: "",
    banner_image_mob_name: "",
    banner_image_mob_error: false,
    banner_title: "",
    banner_title_maxlenth: 64,
    banner_title_error: false,
  };

  // Component state
  const [banner, setBanner] = useState(bannerDefault);
  const [deskImage, setDeskImage] = useState("");
  const [mobImage, setMobImage] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Track blob URLs for cleanup to prevent memory leaks
  const deskBlobRef = useRef(null);
  const mobBlobRef = useRef(null);

  // Load banner data on component mount
  useEffect(() => {
    const controller = new AbortController();

    getAboutBanner({ signal: controller.signal })
      .then((res) => {
        if (res.data.status === true) {
          const data = res.data.data;

          // Update state with response data
          setBanner((prev) => ({
            ...prev,
            about_banner_section_id: data.about_banner_section_id || 0,
            banner_title: data.banner_title || "",
            banner_image_name: data.banner_image || "",
            banner_image_mob_name: data.banner_image_mob || "",
          }));

          // Set images if available
          if (data.banner_image) {
            setDeskImage(
              pathJoinUrl(storage[0].imageEndUrl, data.banner_image),
            );
          }
          if (data.banner_image_mob) {
            setMobImage(
              pathJoinUrl(storage[0].imageEndUrl, data.banner_image_mob),
            );
          }
        }
      })
      .catch((error) => {
        // Ignore aborted requests (component unmounted)
        if (error?.code === "ERR_CANCELED") return;
        log.error("Failed to load about banner:", error);
        setErrorMsg(
          error?.response?.data?.message ||
            "Failed to load banner data. Please refresh the page.",
        );
      })
      .finally(() => {
        setInitialLoading(false);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke blob URLs on unmount to free memory
  useEffect(() => {
    return () => {
      if (deskBlobRef.current) URL.revokeObjectURL(deskBlobRef.current);
      if (mobBlobRef.current) URL.revokeObjectURL(mobBlobRef.current);
    };
  }, []);

  // Handle input field changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSuccessMsg("");
    setErrorMsg("");

    setBanner((prev) => {
      // Only update if within character limit
      if (
        name === "banner_title" &&
        value.length <= prev.banner_title_maxlenth
      ) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  // Handle desktop image upload
  const handleDeskImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      // Revoke previous blob URL before creating a new one
      if (deskBlobRef.current) URL.revokeObjectURL(deskBlobRef.current);
      const blobUrl = URL.createObjectURL(file);
      deskBlobRef.current = blobUrl;
      setDeskImage(blobUrl);
      setBanner((prev) => ({
        ...prev,
        banner_image: file,
        banner_image_name: file.name,
        banner_image_error: false,
      }));
    }
  };

  // Handle mobile image upload
  const handleMobImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      // Revoke previous blob URL before creating a new one
      if (mobBlobRef.current) URL.revokeObjectURL(mobBlobRef.current);
      const blobUrl = URL.createObjectURL(file);
      mobBlobRef.current = blobUrl;
      setMobImage(blobUrl);
      setBanner((prev) => ({
        ...prev,
        banner_image_mob: file,
        banner_image_mob_name: file.name,
        banner_image_mob_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setBanner((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Form submission
  const submitBanner = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate form
    if (!banner.banner_title) {
      setBanner((prev) => ({
        ...prev,
        banner_title_error: true,
      }));
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("about_banner_section_id", banner.about_banner_section_id);
    formData.append("banner_image", banner.banner_image);
    formData.append("banner_image_mob", banner.banner_image_mob);
    formData.append("banner_title", banner.banner_title);

    // Determine if this is a new record or update
    const isNew = banner.about_banner_section_id === 0;

    // Submit to API
    updateAboutBanner(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg(
            isNew
              ? "Banner Added Successfully!"
              : "Banner Updated Successfully!",
          );

          // Hide success message after delay
          setTimeout(() => {
            setSuccessMsg("");
          }, 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (initialLoading) {
    return (
      <ToolBody title="About Banner">
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </ToolBody>
    );
  }

  return (
    <ToolBody title="About Banner">
      <Stack spacing={4}>
        {/* Title Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Banner Title
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <LimitedTextField
                name="banner_title"
                label="Title"
                value={banner.banner_title}
                onChange={handleInputChange}
                maxLength={banner.banner_title_maxlenth}
                error={banner.banner_title_error}
                errorText="Please enter Title!"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Images Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Banner Images
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FileUploadArea
                title="Banner Image For Desktop"
                description={
                  <>
                    <span>Best image Resolution: 1920px X 836px</span>
                    <br />
                    <span>Supported files types: JPG, JPEG & PNG</span>
                  </>
                }
                previewUrl={deskImage}
                fileName={banner.banner_image_name}
                onChange={handleDeskImageChange}
                deleteRequest={{
                  resource: "about-banner",
                  id: banner.about_banner_section_id,
                  field: "banner_image",
                }}
                onRemove={() => clearUpload("banner_image", setDeskImage)}
                accept=".png,.jpg,.jpeg"
                error={banner.banner_image_error}
                errorText="Please upload Desktop Image!"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FileUploadArea
                title="Banner Image For Mobile"
                description={
                  <>
                    <span>Best image Resolution: 400px X 350px</span>
                    <br />
                    <span>Supported files types: JPG, JPEG & PNG</span>
                  </>
                }
                previewUrl={mobImage}
                fileName={banner.banner_image_mob_name}
                onChange={handleMobImageChange}
                deleteRequest={{
                  resource: "about-banner",
                  id: banner.about_banner_section_id,
                  field: "banner_image_mob",
                }}
                onRemove={() =>
                  clearUpload("banner_image_mob", setMobImage)
                }
                accept=".png,.jpg,.jpeg"
                error={banner.banner_image_mob_error}
                errorText="Please upload Mobile Image!"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Form Actions */}
        <FormActionBar
          onSubmit={submitBanner}
          isSubmitting={loading}
          isEdit={banner.about_banner_section_id !== 0}
          successMessage={successMsg}
          errorMessage={errorMsg}
          submitLabel="Update"
        />
      </Stack>
    </ToolBody>
  );
};

export default About;
